import type { ObservabilityReport } from '@module-federation/observability-plugin'
import type { logger } from '@persuit/ui-logger'

type MfLogger = Pick<typeof logger, 'error' | 'warn'>

/**
 * Without this, a Module Federation loading failure (a bad shared-version
 * match, a failed manifest fetch, or the remote's own module throwing during
 * evaluation) is invisible: the federated import just resolves to no value.
 */
export function reportMfObservability(log: MfLogger, report: ObservabilityReport): void {
	if (report.status === 'error' || report.summary.outcome === 'recovered') {
		log.error(
			new Error(report.errorMessage ?? `Module Federation load failed: ${report.summary.outcome}`),
			{
				extra: {
					traceId: report.traceId,
					failedPhase: report.failedPhase,
					errorCode: report.errorCode,
					outcome: report.summary.outcome,
					diagnosis: report.diagnosis,
					remote: report.remote,
					shared: report.shared,
				},
			},
		)
		return
	}

	// A remote's own copy of a shared package winning resolution means the remote is running
	// on a dependency version the host never vetted.
	const shared = report.shared
	if (
		report.summary.outcome === 'shared-resolved' &&
		shared?.provider &&
		report.hostName &&
		shared.provider !== report.hostName
	) {
		log.warn(
			new Error(
				`Module Federation: ${shared.name} resolved to ${shared.provider}@${shared.selectedVersion}, not the host`,
			),
			{
				extra: {
					traceId: report.traceId,
					provider: shared.provider,
					selectedVersion: shared.selectedVersion,
					requiredVersion: shared.requiredVersion,
					availableVersions: shared.availableVersions,
					useIn: shared.useIn,
				},
			},
		)
	}
}
